import type {
  PostingsV3DataPoint,
  PostingsV3DataQuery,
} from '@revelio/data-access';

import type { PostingSecondaryDimension } from '../../PageConfigProvider/types/dimension';
import type { PostingMetricValues } from '../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataSet,
} from '../types';

/** ================================
 * Conversion Functions
 ================================ */

/** Convert Postings Data Entry */
export const convertPostingsV3Data = ({
  data,
  metric,
  secondaryDimension,
  weekRefs,
  exchangeRate = 1,
}: {
  data: PostingsV3DataQuery | undefined;
  metric: PostingMetricValues;
  secondaryDimension?: PostingSecondaryDimension;
  weekRefs: ReadonlyMap<number, string>;
  exchangeRate?: number;
}): TimeseriesDataSet | BreakdownSingleDataSet | SingleDataSet => {
  if (metric === 'postings_volume') {
    return convertPostingsVolumeOverTime({ data, weekRefs });
  }

  if (secondaryDimension === 'timeseries') {
    return convertPostingsFieldOverTime({
      data,
      metric,
      weekRefs,
      exchangeRate,
    });
  }

  if (secondaryDimension) {
    return convertPostingsTopNCounts({ data, secondaryDimension });
  }

  return convertPostingsMostRecent({ data, metric, exchangeRate });
};

/** Postings Volume (Active / New / Removed) Over Time */
const convertPostingsVolumeOverTime = ({
  data,
  weekRefs,
}: {
  data: PostingsV3DataQuery | undefined;
  weekRefs: ReadonlyMap<number, string>;
}): TimeseriesDataSet => {
  const entities = data?.postings_v3?.entities ?? [];

  const series: TimeseriesDataSet['data'] = (
    [
      ['Active', 'active'],
      ['New', 'added'],
      ['Removed', 'removed'],
    ] as const
  ).map(([dim, field]) => ({
    id: dim,
    dim,
    timeseries: toTimeseries({
      points: sumFieldAcrossEntities({ entities, field }),
      weekRefs,
    }),
  }));

  return { type: DataType.TIMESERIES, data: series };
};

/** Postings Over Time */
const convertPostingsFieldOverTime = ({
  data,
  metric,
  weekRefs,
  exchangeRate = 1,
}: {
  data: PostingsV3DataQuery | undefined;
  metric: PostingMetricValues;
  weekRefs: ReadonlyMap<number, string>;
  exchangeRate?: number;
}): TimeseriesDataSet => {
  const entities = data?.postings_v3?.entities ?? [];
  const field = getPostingMetricField(metric);
  const isSalary = metric === 'posting_salary';

  const seriesData: TimeseriesDataSet['data'] = entities.map((entity) => ({
    id: String(entity.index),
    dim: entity.short_name,
    timeseries: toTimeseries({
      points: entity.metrics[field],
      weekRefs,
      isSalary,
      exchangeRate,
    }),
  }));

  return { type: DataType.TIMESERIES, data: seriesData };
};

/** Postings Most Recent */
const convertPostingsMostRecent = ({
  data,
  metric,
  exchangeRate = 1,
}: {
  data: PostingsV3DataQuery | undefined;
  metric: PostingMetricValues;
  exchangeRate?: number;
}): SingleDataSet => {
  const entities = data?.postings_v3?.entities ?? [];
  const field = getPostingMetricField(metric);
  const isSalary = metric === 'posting_salary';

  const singleData: SingleDataPoint[] = entities.map((entity) => {
    const points = entity.metrics?.[field] ?? [];
    const mostRecent = points?.[points.length - 1];

    const value = (() => {
      if (!mostRecent) return null;
      if (isSalary) return mostRecent.val * exchangeRate;
      return mostRecent.val;
    })();

    return { dim: entity.short_name, id: String(entity.index), value };
  });

  return { type: DataType.SINGLE, data: singleData };
};

/** ================================
 * Helper Functions
 ================================ */
type PostingsV3Entities = NonNullable<
  PostingsV3DataQuery['postings_v3']
>['entities'];
type PostingsV3TimeseriesField = Extract<
  keyof PostingsV3Entities[number]['metrics'],
  'added' | 'removed' | 'active' | 'time_to_fill' | 'salary'
>;

const getPostingMetricField = (
  metric: PostingMetricValues
): PostingsV3TimeseriesField => {
  switch (metric) {
    case 'posting_salary':
      return 'salary';
    case 'time_to_fill':
    case 'time_to_fill_percentile':
      return 'time_to_fill';
    case 'postings_new':
      return 'added';
    case 'postings_removed':
      return 'removed';
    case 'postings_active':
    case 'postings_volume':
      return 'active';
  }
};

const toTimeseries = ({
  points,
  weekRefs,
  isSalary = false,
  exchangeRate = 1,
}: {
  points: ReadonlyArray<Pick<PostingsV3DataPoint, 'id' | 'val'>>;
  weekRefs: ReadonlyMap<number, string>;
  isSalary?: boolean;
  exchangeRate?: number;
}): TimeseriesData['timeseries'] =>
  points
    .map((point) => {
      const date = weekRefs.get(point.id) ?? '';
      if (date === '') return null;
      return {
        date,
        value: isSalary ? point.val * exchangeRate : point.val,
      };
    })
    .filter(
      (entry): entry is { date: string; value: number } => entry !== null
    );

const sumFieldAcrossEntities = ({
  entities,
  field,
}: {
  entities: PostingsV3Entities;
  field: Extract<PostingsV3TimeseriesField, 'active' | 'added' | 'removed'>;
}): Pick<PostingsV3DataPoint, 'id' | 'val'>[] => {
  const totalsByWeekId = new Map<number, number>();

  for (const entity of entities) {
    for (const point of entity.metrics[field]) {
      totalsByWeekId.set(
        point.id,
        (totalsByWeekId.get(point.id) ?? 0) + point.val
      );
    }
  }

  return Array.from(totalsByWeekId, ([id, val]) => ({ id, val }));
};

const TOP_N_COUNT_UPPER_LIMIT = 20;
const convertPostingsTopNCounts = ({
  data,
  secondaryDimension,
}: {
  data: PostingsV3DataQuery | undefined;
  secondaryDimension: Extract<PostingSecondaryDimension, 'company' | 'role'>;
}): BreakdownSingleDataSet => {
  const entities = data?.postings_v3?.entities ?? [];
  const breakdownDataPoints: Record<
    string,
    BreakdownData<SingleDataPoint>
  > = {};

  const topNField = (() => {
    switch (secondaryDimension) {
      case 'company':
        return 'company_counts';
      case 'role':
        return 'role_counts';
    }
  })();

  const topNCountLimit = Math.floor(TOP_N_COUNT_UPPER_LIMIT / entities.length);
  for (const entity of entities) {
    const counts = entity.metrics?.[topNField] ?? [];
    const limitedCounts = counts.slice(0, topNCountLimit);

    for (const count of limitedCounts) {
      const dim = count.short_name;
      breakdownDataPoints[dim] = {
        dim,
        breakdown: [
          ...(breakdownDataPoints[dim]?.breakdown ?? []),
          {
            dim: entity.short_name,
            longName: entity.long_name,
            value: count.count,
            share: count.share,
            id: String(entity.index),
          },
        ],
      };
    }
  }

  return {
    type: DataType.BREAKDOWN_SINGLE,
    data: Object.values(breakdownDataPoints),
  };
};
